<template>
  <main class="shop-products">
    <!-- ///// Product Column Header ///// -->
    <header class="shop-products-header">
      <div class="shop-products-header-wrapper">
        <!-- ///// Sort ///// -->
        <div class="shop-sort">
          <label>
            <symbol-icon icon-name="uiSort" view-box="0 0 24 24" />
          </label>
          <ais-sort-by :items="sortItems">
            <template v-slot:default="{ items, refine, currentRefinement, hasNoResults }">
              <select @change="refine($event.currentTarget.value)">
                <option v-for="item in items" :key="item.value" class="ais-SortBy-option" :value="item.value" :selected="item.value === currentRefinement">
                  {{ item.label }}
                </option>
              </select>
            </template>
          </ais-sort-by>
        </div>

        <!-- ///// Product Image/Artwork Toggle ///// -->
        <div class="shop-display" v-show="true">
          <label>
            <symbol-icon icon-name="uiEye" view-box="0 0 24 24" />
          </label>
          <div class="shop-display-toggle">
            <a class="shop-display-toggle-link photo" :class="{ active: displayMode === 'photo' }" href="#" @click.prevent="displayMode = 'photo'">Photo</a>
            <a class="shop-display-toggle-link artwork" :class="{ active: displayMode === 'artwork' }" href="#" @click.prevent="displayMode = 'artwork'">Artwork</a>
          </div>
        </div>

        <!-- ///// Toggles Filter Options on Mobile (they’re hiddedn by default) ///// -->
        <button class="open-controls" :class="{ active: controlState.showMobileControls }" @click="controlState.showMobileControls = !controlState.showMobileControls">Filters</button>

        <!-- ///// Mobile Search ///// -->
        <ais-search-box placeholder="Filter by keyword...">
          <debounced-search-box />
        </ais-search-box>
      </div>
    </header>

    <div class="shop-products-sub-header">
      <!-- ///// Selected Filter Pills ///// -->
      <ais-current-refinements :transform-items="transformCurrentRefinements" :excluded-attributes="excludedAttributes">
        <template v-slot:item="{ item, refine, createURL }">
          <span class="ais-CurrentRefinements-item">
            <span class="ais-CurrentRefinements-label" :class="{ 'color-swatch': item.label === 'Color' }">
              <i v-if="item.label === 'Color'" :class="readableColor(item.refinements[0].label)"></i>
              <strong>
                <template v-if="item.label === 'Color'">
                  {{ readableColor(item.refinements[0].label) }}
                </template>
                <template v-else>
                  {{ item.refinements[0].label }}
                </template>
              </strong>
            </span>
            <button class="ais-CurrentRefinements-delete" @click="item.refine(item.refinements[0])">✕</button>
          </span>
        </template>
      </ais-current-refinements>

      <!-- ///// Clear Filters Button ///// -->
      <ais-clear-refinements>
        <template v-slot:default="{ canRefine, refine, createURL }">
          <button type="reset" :class="['ais-ClearRefinements-button', !canRefine && `ais-ClearRefinements-button--disabled`]" :disabled="!canRefine" @click.prevent="refine">
            Clear All
            <i class="u-hideOnMobile">Filters</i>
            <span class="x"></span>
          </button>
        </template>
      </ais-clear-refinements>
    </div>

    <!-- ///// Products ///// -->
    <ais-infinite-hits :escape-HTML="true" :class-names="isSparseResults">
      <!-- ///// Single Product Card ///// -->
      <template v-slot:item="{ item }">
        <!-- ///// Product Image ///// -->
        <a class="item-image" :href="variantUrl(item)" :class="{ unavailable: !item.available_for_sale }">
          <ProductImageV2
            :alt="item.product__title"
            :variantId="item.id"
            :detail="displayMode === 'artwork'"
            :hash="item.product__image_cache_hash"
            sizes="(min-width: 1920px) 16vw, (min-width: 1600px) 20vw, (min-width: 1280px) 25vw, (min-width: 480px) 33vw, 50vw"
            :key="`${item.id} ${displayMode}`"
          />
        </a>

        <!-- ///// Product Details ///// -->
        <div
          class="item-details"
          :class="{
            'campaign-item-details': isLiveCampaignWithLegacySupport(item),
          }"
        >
          <h2>
            <a :href="variantUrl(item)">
              <ais-highlight :hit="item" attribute="product__title" />
            </a>
          </h2>
          <h3>
            <template v-if="!isPeoplePage">
              <a :href="sellerUrl(item)" class="seller-name">
                <ais-highlight :hit="item" attribute="product__seller__name" />
              </a>
              <span class="divider-line"></span>
            </template>
            <span class="time" v-if="isLiveCampaignWithLegacySupport(item)">
              {{ timeRemaining(item.product__expires_at) }}
            </span>
            <span class="time" v-else-if="item.product__sales_method === 'Stock' || item.product__sales_method === 'On-Demand'">
              {{ item.product__sales_method }}
            </span>
            <span class="time" v-else-if="!item.available_for_sale">Sold out!</span>
            <span class="time" v-else-if="item.product__sales_method === 'Pre-Order'">Pre-Order</span>
          </h3>

          <template v-if="shouldShowTags(item)">
            <div class="tags">
              Tags:
              <a v-for="(tag, index) in item._highlightResult.product__tags__names" :href="tagUrl(item, index)">
                <span v-if="tag.value.includes('<mark>')" class="ais-Highlight" v-html="addClassToMark(tag.value)"></span>
              </a>
            </div>
          </template>

          <template v-if="shouldShowDescriptionSnippet(item)">
            <div class="description">
              Desc: “
              <ais-snippet :hit="item" attribute="product__description"></ais-snippet>
              ”
            </div>
          </template>
        </div>
      </template>
    </ais-infinite-hits>

    <!-- ///// Empty State ///// -->
    <ais-state-results>
      <template v-slot:default="{ state: { query }, results: { hits } }">
        <div v-show="hits.length === 0">
          <h4>Sorry, there are no results.</h4>
          <p>Try removing some filters or using a different search term.</p>
        </div>
      </template>
    </ais-state-results>
  </main>
</template>
<script>
import { defineAsyncComponent } from "vue";
import { DateTime } from "luxon";
const DebouncedSearchBox = defineAsyncComponent(() => import("./debounced-search-box.vue"));
const ProductImageV2 = defineAsyncComponent(() => import("@/product/components/product-image-v2.vue"));
const SymbolIcon = defineAsyncComponent(() => import("@/components/symbol-icon.vue"));

export default {
  components: { DebouncedSearchBox, ProductImageV2, SymbolIcon },

  mixins: [],

  inject: {
    controlState: {},
    instantSearchInstance: {
      from: "$_ais_instantSearchInstance",
      default() {
        const tag = this.$options._componentTag;
        throw new TypeError(`It looks like you forgot to wrap your search component "<search-controls>" inside of an "<ais-instant-search>" component.`);
      },
    },
  },

  props: {
    showBuyItNow: {
      type: Boolean,
      required: true,
    },

    showCollections: {
      type: Boolean,
      required: true,
    },

    isPeoplePage: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      displayMode: "photo",

      sortItems: [
        {
          value: "variants:product__week_sold:desc",
          label: "Most Popular Now",
        },
        {
          value: "variants:product__launched_at:desc",
          label: "Newest",
        },
        {
          value: "variants:product__random:asc",
          label: "Random",
        },
      ],
    };
  },

  computed: {
    excludedAttributes() {
      const attributes = [];

      if (!this.showBuyItNow) {
        // attributes.push("available_for_sale");
      }

      if (!this.showCollections) {
        attributes.push("product__collections__names");
      }

      return attributes;
    },

    isSparseResults() {
      var sparseResultsClass = {};

      if (this.isPeoplePage && this?.instantSearchInstance?.renderState[this?.instantSearchInstance?.indexName]?.infiniteHits?.results?.nbHits) {
        if (this.instantSearchInstance.renderState[this.instantSearchInstance.indexName].infiniteHits.results.nbHits <= 6) {
          sparseResultsClass = { "ais-InfiniteHits": "ais-InfiniteHits--sparse" };
        }
      }

      return sparseResultsClass;
    },
  },

  methods: {
    readableColor(color) {
      let name = color;

      switch (color) {
        case "#545454":
          name = "Charcoal";
          break;
        case "#000":
          name = "Black";
          break;
        case "#fff":
          name = "White";
          break;
        case "#a9a9a9":
          name = "Silver";
          break;
        case "#0054ff":
          name = "Blue";
          break;
        case "#f00":
          name = "Red";
          break;
        case "#00a9ff":
          name = "Aqua";
          break;
        case "#fe0":
          name = "Yellow";
          break;
        case "#2aff00":
          name = "Green";
          break;
        case "#7f00ff":
          name = "Purple";
          break;
        case "#f60":
          name = "Orange";
          break;
        case "#ff007f":
          name = "Fuschia";
          break;
      }

      return name;
    },

    transformCurrentRefinements(items) {
      items = items.map((item) => {
        const outputItem = { ...item };

        switch (outputItem.attribute) {
          case "categories__lvl0":
            outputItem.label = "Category";
            break;
          case "sex_readable":
            outputItem.label = "Cut";
            break;
          case "icon_color_palettized":
            outputItem.label = "Color";
            break;
          case "material_readable":
            outputItem.label = "Material";
            break;
          case "available_for_sale":
            outputItem.refinements[0].label = "Buy it Now";
            break;
          case "manufacturer_readable":
            outputItem.refinements[0].label = "Buy it on Blank";
            break;
          case "product__collections__names":
            outputItem.refinements = outputItem.refinements.map((refinement) => {
              switch (refinement.label) {
                case "Tis the Season":
                  refinement.label = "’Tis the Season";
              }

              return refinement;
            });
            break;
        }

        return outputItem;
      });

      return items;
    },

    shouldShowTags(item) {
      return item?._highlightResult?.product__tags__names?.some((tagName) => tagName.value.includes("<mark>"));
    },

    shouldShowDescriptionSnippet(item) {
      return item?._snippetResult?.product__description?.value.includes("<mark>");
    },

    variantUrl(item) {
      return `${SITE_URL}${item.url}`;
    },

    sellerUrl(item) {
      return `${SITE_URL}${item.product__seller__url}`;
    },

    tagUrl(item, index) {
      const url = this.removeEm(item?._highlightResult?.product__tags__urls[index].value);
      return `${SITE_URL}${url}`;
    },

    removeEm(value) {
      return value.replace(new RegExp("<mark>|</mark>", "g"), "");
    },

    addClassToMark(attributeValue) {
      return attributeValue.replace(new RegExp("<mark>", "g"), `<mark class="ais-Highlight-highlighted">`);
      //.replace(new RegExp('</mark>', 'g'), `</mark>`);
    },

    timeRemaining(expiration) {
      const diffInSeconds = DateTime.fromISO(expiration).diff(DateTime.now(), "seconds");

      if (diffInSeconds > 0) {
        return `Ends ${DateTime.fromISO(expiration).toRelative()}`;
      } else {
        return "Ended";
      }
    },

    isLiveCampaignWithLegacySupport(item) {
      const isLegacyCampaign = item.product__status && ["live"].includes(item.product__status);
      const isModernCampaign = item.product__sales_method && item.product__preorder_status && ["Pre-Order"].includes(item.product__sales_method) && ["live"].includes(item.product__preorder_status);

      return isLegacyCampaign || isModernCampaign;
    },
  },
};
</script>
